import { useId } from 'react';
import { PhotoIcon } from '@heroicons/react/24/outline';
import { OverlayScrollArea, formatScanDate, scanTakenAt, type DmScanSummary } from '@odo/shared-ui';
import { ErrorBanner } from '../DmBanners';
import { DmBtnSecondary, dmInputClasses } from '../DmFormPrimitives';
import { scanOptionLabel, type MarkupPhoto } from './scanPhotoOptions';
import { ScanPhotoGrid, ScanPhotoGridSkeleton } from './ScanPhotoGrid';
import { useMarkupScanPhotos, type MarkupPhotosState } from './useMarkupScanPhotos';

interface ScanPhotoPickerProps {
    patientId: string;
    dmId: number | null;
    selectedScanId: number | null;
    onSelectScan: (scanId: number) => void;
    onPick: (photo: MarkupPhoto, scan: DmScanSummary) => void;
    onCancel: () => void;
}

function EmptyState({ text }: { text: string }) {
    return (
        <div className="flex flex-col items-center justify-center gap-2 py-16 text-sm text-gray-500">
            <PhotoIcon className="w-8 h-8 text-gray-300" />
            {text}
        </div>
    );
}

interface PickerBodyProps {
    photos: MarkupPhotosState;
    onPick: (photo: MarkupPhoto) => void;
}

function PickerBody({ photos, onPick }: PickerBodyProps) {
    switch (photos.status) {
        case 'loading':
            return <ScanPhotoGridSkeleton />;
        case 'error':
            return <ErrorBanner message={photos.message} onRetry={photos.retry} />;
        case 'no-scans':
            return <EmptyState text="This patient has no Dental Monitoring scans yet." />;
        case 'no-photos':
            return <EmptyState text="This scan has no photos. Pick another scan date." />;
        case 'ready':
            return <ScanPhotoGrid photos={photos.photos} onPick={onPick} />;
    }
}

function ScanPhotoPicker({
    patientId,
    dmId,
    selectedScanId,
    onSelectScan,
    onPick,
    onCancel
}: ScanPhotoPickerProps) {
    const selectId = useId();
    const { scans, scan, photos } = useMarkupScanPhotos({ patientId, dmId, selectedScanId });

    return (
        <>
            <div className="flex flex-wrap items-center gap-3 px-6 py-3 border-b border-gray-200">
                <label htmlFor={selectId} className="text-sm font-semibold text-gray-700">
                    Scan
                </label>
                <div className="w-full max-w-xs">
                    <select
                        id={selectId}
                        value={scan?.scanId ?? ''}
                        onChange={(e) => onSelectScan(Number(e.target.value))}
                        disabled={scans.length === 0}
                        className={`${dmInputClasses} disabled:bg-gray-50`}
                    >
                        {scans.map((item, index) => (
                            <option key={item.scanId} value={item.scanId}>
                                {`${scanOptionLabel(item, formatScanDate(scanTakenAt(item)))}${index === 0 ? ' (latest)' : ''}`}
                            </option>
                        ))}
                    </select>
                </div>
                <span className="text-sm text-gray-500">Choose a photo to mark up.</span>
            </div>
            <OverlayScrollArea
                className="flex-1 min-h-0"
                contentClassName="px-6 py-5"
                trackInsetPx={4}
            >
                <PickerBody photos={photos} onPick={(photo) => scan && onPick(photo, scan)} />
            </OverlayScrollArea>
            <footer className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-200">
                <DmBtnSecondary onClick={onCancel}>Cancel</DmBtnSecondary>
            </footer>
        </>
    );
}

export { ScanPhotoPicker };
